import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';
import type {
  AreasUpdate,
  EmployeeAssignmentsUpdate,
  EmployeeProfileUpdate,
  PositionsUpdate,
} from '@/types/database.types';

import * as areasApi from './api/areas';
import * as contactsApi from './api/contacts';
import type { EmployeeContact, EmployeeContactInput } from './api/contacts';
import * as employeesApi from './api/employees';
import * as positionsApi from './api/positions';

const KEYS = {
  areas: ['core', 'areas'] as const,
  positions: ['core', 'positions'] as const,
  directory: ['core', 'v_employee_directory'] as const,
  directoryEntry: (userId: string) =>
    ['core', 'v_employee_directory', userId] as const,
  profile: (userId: string) =>
    ['core', 'employee_profile', userId] as const,
  assignments: (userId: string) =>
    ['core', 'employee_assignments', userId] as const,
  currentPrimaryArea: ['core', 'current_primary_area'] as const,
};

// ============================================================================
// Áreas
// ============================================================================

export function useAreas(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEYS.areas,
    queryFn: areasApi.listAreas,
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
  });
}

/**
 * Área primaria del user actual · RPC core.current_primary_area().
 *
 * Devuelve el `area_id` (uuid string) de la asignación con `is_primary=true`
 * y `ended_at IS NULL` del usuario logueado. Si no tiene asignaciones
 * vigentes, devuelve null. RLS-safe — la RPC consulta auth.uid() internamente.
 *
 * Usado para pre-rellenar el área en TaskFormDialog (Bug 1a) y en
 * cualquier flujo donde "tu área por defecto" deba inferirse.
 */
export function useCurrentPrimaryArea() {
  return useQuery({
    queryKey: KEYS.currentPrimaryArea,
    queryFn: async () => {
      const { data, error } = await (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        supabase.schema('core' as any) as any
      ).rpc('current_primary_area');
      if (error) throw error;
      return (data as string | null) ?? null;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Áreas donde el user actual tiene asignación vigente (ended_at IS NULL).
 *
 * El empleado solo puede crear tareas de equipo en SUS áreas — no en
 * todas. Si un user tiene `areas.manage` (admin/superadmin) usa `useAreas()`
 * que devuelve todas.
 *
 * RLS-safe — la query filtra por user_id=auth.uid() explícitamente y la
 * policy de `employee_assignments` (lectura propia) la garantiza.
 */
export function useMyAreas() {
  const { session } = useSupabase();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['core', 'my_areas', userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      // Lee assignments del user + join a areas (RLS en assignments + areas).
      // El join "areas!inner" no está bien tipado por database.types.ts
      // hand-curated → cast a unknown primero.
      const { data, error } = await supabase
        .from('employee_assignments')
        .select('area_id, is_primary, areas!inner(id, name, is_active)')
        .eq('user_id', userId)
        .is('ended_at', null);
      if (error) throw error;

      const seen = new Set<string>();
      const out: { id: string; name: string; is_primary: boolean }[] = [];
      for (const row of (data as unknown as Array<{
        area_id: string;
        is_primary: boolean;
        areas: { id: string; name: string; is_active: boolean } | null;
      }> | null) ?? []) {
        if (!row.areas || !row.areas.is_active) continue;
        if (seen.has(row.area_id)) continue;
        seen.add(row.area_id);
        out.push({
          id: row.area_id,
          name: row.areas.name,
          is_primary: row.is_primary,
        });
      }
      // Primary primero
      out.sort((a, b) => Number(b.is_primary) - Number(a.is_primary));
      return out;
    },
  });
}

export function useCreateArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: areasApi.createArea,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.areas });
      toast.success('Área creada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo crear el área', { description: error.message }),
  });
}

export function useUpdateArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AreasUpdate }) =>
      areasApi.updateArea(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.areas });
      qc.invalidateQueries({ queryKey: KEYS.directory });
      toast.success('Área actualizada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo actualizar', { description: error.message }),
  });
}

export function useDeleteArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: areasApi.deleteArea,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.areas });
      toast.success('Área eliminada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo eliminar', {
        description:
          error.message.includes('foreign key') || error.message.includes('violates')
            ? 'El área tiene empleados asignados. Cerrá esas asignaciones primero.'
            : error.message,
      }),
  });
}

// ============================================================================
// Cargos
// ============================================================================

export function usePositions(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEYS.positions,
    queryFn: positionsApi.listPositions,
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useCreatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: positionsApi.createPosition,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.positions });
      toast.success('Cargo creado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo crear el cargo', { description: error.message }),
  });
}

export function useUpdatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: PositionsUpdate;
    }) => positionsApi.updatePosition(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.positions });
      qc.invalidateQueries({ queryKey: KEYS.directory });
      toast.success('Cargo actualizado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo actualizar', { description: error.message }),
  });
}

export function useDeletePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: positionsApi.deletePosition,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.positions });
      toast.success('Cargo eliminado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo eliminar', {
        description:
          error.message.includes('foreign key') || error.message.includes('violates')
            ? 'El cargo está en uso por una asignación vigente.'
            : error.message,
      }),
  });
}

// ============================================================================
// Empleados — directorio y perfil
// ============================================================================

export function useEmployeesDirectory(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEYS.directory,
    queryFn: employeesApi.listDirectory,
    enabled: opts?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useEmployeeDirectoryEntry(userId: string | undefined) {
  return useQuery({
    queryKey: KEYS.directoryEntry(userId ?? ''),
    queryFn: () => employeesApi.getDirectoryEntry(userId!),
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

/**
 * Perfil del usuario actualmente logueado. Read-only para el empleado: la
 * edición ocurre solo desde el panel admin con permission employees.manage.
 */
export function useMyEmployeeProfile() {
  const { user } = useSupabase();
  return useQuery({
    queryKey: KEYS.profile(user?.id ?? ''),
    queryFn: () => employeesApi.getMyProfile(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 60_000,
  });
}

export function useEmployeeProfile(userId: string | undefined) {
  return useQuery({
    queryKey: KEYS.profile(userId ?? ''),
    queryFn: () => employeesApi.getProfile(userId!),
    enabled: Boolean(userId),
  });
}

export function useUpsertEmployeeProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: employeesApi.upsertProfile,
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: KEYS.directory });
      qc.invalidateQueries({ queryKey: KEYS.profile(row.user_id) });
      qc.invalidateQueries({ queryKey: KEYS.directoryEntry(row.user_id) });
      toast.success('Perfil guardado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo guardar el perfil', { description: error.message }),
  });
}

export function useUpdateEmployeeProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      patch,
    }: {
      userId: string;
      patch: EmployeeProfileUpdate;
    }) => employeesApi.updateProfile(userId, patch),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: KEYS.directory });
      qc.invalidateQueries({ queryKey: KEYS.profile(row.user_id) });
      qc.invalidateQueries({ queryKey: KEYS.directoryEntry(row.user_id) });
      toast.success('Perfil actualizado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo actualizar el perfil', {
        description: error.message,
      }),
  });
}

// ---------- Asignaciones ----------

export function useEmployeeAssignments(userId: string | undefined) {
  return useQuery({
    queryKey: KEYS.assignments(userId ?? ''),
    queryFn: () => employeesApi.listAssignments(userId!),
    enabled: Boolean(userId),
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: employeesApi.createAssignment,
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: KEYS.assignments(row.user_id) });
      qc.invalidateQueries({ queryKey: KEYS.directory });
      qc.invalidateQueries({ queryKey: KEYS.directoryEntry(row.user_id) });
      toast.success('Asignación agregada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo agregar la asignación', {
        description: error.message,
      }),
  });
}

export function useUpdateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: EmployeeAssignmentsUpdate;
    }) => employeesApi.updateAssignment(id, patch),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: KEYS.assignments(row.user_id) });
      qc.invalidateQueries({ queryKey: KEYS.directory });
      qc.invalidateQueries({ queryKey: KEYS.directoryEntry(row.user_id) });
      toast.success('Asignación actualizada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo actualizar', { description: error.message }),
  });
}

export function useCloseAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; userId: string }) =>
      employeesApi.closeAssignment(id),
    onSuccess: (row, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.assignments(vars.userId) });
      qc.invalidateQueries({ queryKey: KEYS.directory });
      qc.invalidateQueries({ queryKey: KEYS.directoryEntry(row.user_id) });
      toast.success('Asignación cerrada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo cerrar', { description: error.message }),
  });
}

// ---------- Contactos ----------

const contactsKey = (userId: string) => ['core', 'employee_contacts', userId] as const;

export function useEmployeeContacts(userId: string | undefined) {
  return useQuery({
    queryKey: contactsKey(userId ?? ''),
    queryFn: () => contactsApi.listContacts(userId!),
    enabled: Boolean(userId),
  });
}

function useInvalidarContactos() {
  const qc = useQueryClient();
  return (userId: string) => {
    qc.invalidateQueries({ queryKey: contactsKey(userId) });
    // El principal se copia a employee_profile.phone por disparador.
    qc.invalidateQueries({ queryKey: KEYS.profile(userId) });
    qc.invalidateQueries({ queryKey: KEYS.directoryEntry(userId) });
    qc.invalidateQueries({ queryKey: KEYS.directory });
  };
}

export function useSaveEmployeeContact() {
  const invalidar = useInvalidarContactos();
  return useMutation({
    mutationFn: ({
      userId,
      contact,
      input,
    }: {
      userId: string;
      contact: EmployeeContact | null;
      input: EmployeeContactInput;
    }) =>
      contact
        ? contactsApi.updateContact(contact, input)
        : contactsApi.createContact(userId, input),
    onSuccess: (row) => {
      invalidar(row.user_id);
      toast.success('Contacto guardado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo guardar el contacto', { description: error.message }),
  });
}

export function useDeleteEmployeeContact() {
  const invalidar = useInvalidarContactos();
  return useMutation({
    mutationFn: (contact: EmployeeContact) => contactsApi.deleteContact(contact.id),
    onSuccess: (_row, contact) => {
      invalidar(contact.user_id);
      toast.success('Contacto eliminado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo eliminar el contacto', { description: error.message }),
  });
}

// ---------- Cuentas vinculadas ----------

export function useEmployeeLinkedAccounts(userId: string | undefined) {
  return useQuery({
    queryKey: ['core', 'employee_linked_accounts', userId ?? ''] as const,
    queryFn: () => contactsApi.getLinkedAccounts(userId!),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });
}

// ---------- Correos de una casilla ----------

export function useMailboxMessages(connectionId: string | null) {
  return useQuery({
    queryKey: ['core', 'employee_mailbox_messages', connectionId ?? ''] as const,
    queryFn: () => contactsApi.listMailboxMessages(connectionId!),
    enabled: Boolean(connectionId),
    staleTime: 60_000,
  });
}

export function useMailboxMessage(emailId: string | null) {
  return useQuery({
    queryKey: ['core', 'employee_mailbox_message', emailId ?? ''] as const,
    queryFn: () => contactsApi.getMailboxMessage(emailId!),
    enabled: Boolean(emailId),
    staleTime: 5 * 60_000,
  });
}
